import React, { useState, useContext, useEffect } from "react";
import { useQuery } from "react-query";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Button } from "../../../ui/button";
import { Badge } from "../../../ui/badge";
import { getCoachAICallReport } from "../../../../apis/callLogs";
import SharedDataContext from "../../../../SharedDataContext";
import { Skeleton } from "../../../ui/skeleton";
import NoData from "../../../NoData";
import { useCaptureEvent } from "../../../../hooks/useCaptureEvent";
import Image from "../../../Tags/Image";
import TranscriptPanel from "./components/TranscriptPanel";
import SessionRightPanel from "./components/RightPanel";

export default function CoachAISessionReport({ sessionId }) {
  const navigate = useNavigate();
  const { workspaceSlug } = useParams();
  const location = useLocation();
  const userData = useContext(SharedDataContext);
  const { activeWorkspace } = userData || {};
  const captureEvent = useCaptureEvent();

  useEffect(() => {
    if (sessionId && activeWorkspace?.id) {
      captureEvent("CAI_SessionReport_PageView", {
        session_id: sessionId,
        workspace_id: activeWorkspace.id,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, activeWorkspace?.id]);

  const {
    data: apiResponse,
    isLoading,
    isError,
    error,
  } = useQuery(
    ["get_coach_ai_call_report", sessionId],
    () =>
      getCoachAICallReport({
        sessionId,
        options: {
          headers: {
            "X-Workspace-Id": activeWorkspace?.id,
          },
        },
      }),
    {
      enabled: !!sessionId && !!activeWorkspace?.id,
      retry: 1,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      staleTime: 5 * 60 * 1000,
      onError: (err) => {
        console.error("Failed to fetch Coach AI call report:", err);
        captureEvent("CAI_SessionReport_FetchError", {
          session_id: sessionId,
          error: err?.message || "Unknown error",
        });
      },
    }
  );

  const handleGoBack = () => {
    if (location.state?.historyRow) {
      navigate(`/${workspaceSlug}/coach-ai`, {
        state: { openHistoryTab: true },
      });
    } else {
      navigate(-1);
    }
    captureEvent("CAI_SessionReport_BackClick", { session_id: sessionId });
  };

  const formatDateTime = (isoString) => {
    if (!isoString) return "-";
    try {
      const date = new Date(isoString);
      return date.toLocaleString("en-US", {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      });
    } catch (error) {
      return "-";
    }
  };

  const formatDuration = (seconds) => {
    if (!seconds && seconds !== 0) return "-";
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    if (mins === 0) return `${secs}s`;
    return `${mins}m ${secs}s`;
  };

  const getCallTypeBadge = (callType) => {
    if (callType === "cold") {
      return (
        <Badge
          radius="sm"
          variant="white"
          className="border-[#D5D7DA] dark:border-[#373A41] bg-white dark:bg-[#13161B] text-[#414651] dark:text-[#CECFD2] text-sm font-medium"
        >
          <svg
            width="12"
            height="12"
            viewBox="0 0 12 12"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="mr-1"
          >
            <g clipPath="url(#clip0_13070_33319)">
              <path
                d="M9.02918 4.25L2.96701 7.75M9.02918 4.25L9.57822 2.20096M9.02918 4.25L11.0782 4.79904M2.96701 7.75L0.917969 7.20096M2.96701 7.75L2.41797 9.79904M9.02914 7.74993L2.96696 4.24993M9.02914 7.74993L11.0782 7.2009M9.02914 7.74993L9.57823 9.79898M2.96696 4.24993L2.41804 2.20102M2.96696 4.24993L0.91804 4.79909M5.9981 2.5L5.9981 9.5M5.9981 2.5L4.4981 1M5.9981 2.5L7.4981 1M5.9981 9.5L4.4981 11M5.9981 9.5L7.4981 11"
                stroke="#535862"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </g>
            <defs>
              <clipPath id="clip0_13070_33319">
                <rect width="12" height="12" fill="white" />
              </clipPath>
            </defs>
          </svg>
          Cold call
        </Badge>
      );
    } else if (callType === "discovery") {
      return (
        <Badge
          radius="sm"
          variant="gray"
          className="border-[#D5D7DA] dark:border-[#373A41] bg-white dark:bg-[#1F242F] text-[#414651] dark:text-[#CECFD2]"
        >
          <svg
            width="12"
            height="12"
            viewBox="0 0 12 12"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="mr-1"
          >
            <g clipPath="url(#clip0_13070_32519)">
              <path
                d="M10.5 10.5L8.325 8.325M9.5 5.5C9.5 7.70914 7.70914 9.5 5.5 9.5C3.29086 9.5 1.5 7.70914 1.5 5.5C1.5 3.29086 3.29086 1.5 5.5 1.5C7.70914 1.5 9.5 3.29086 9.5 5.5Z"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </g>
            <defs>
              <clipPath id="clip0_13070_32519">
                <rect width="12" height="12" fill="white" />
              </clipPath>
            </defs>
          </svg>
          Discovery call
        </Badge>
      );
    }
    return (
      <Badge radius="sm" variant="gray" className="border-[#D5D7DA] bg-white text-[#414651]">
        {callType || "Unknown"}
      </Badge>
    );
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Skeleton className="h-10 w-10 rounded-full" />
            <div className="space-y-2">
              <Skeleton className="h-6 w-48" />
              <Skeleton className="h-4 w-32" />
            </div>
          </div>
          <Skeleton className="h-10 w-24" />
        </div>
        <div className="space-y-4">
          <Skeleton className="h-8 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      </div>
    );
  }

  if (isError || !apiResponse?.data) {
    return (
      <div className="space-y-6">
        <div className="flex items-center space-x-4">
          <Button variant="ghost" size="sm" onClick={handleGoBack} className="p-2">
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h1 className="text-2xl font-semibold">Session Report</h1>
        </div>
        <NoData
          title="Session report not found"
          description="The session report could not be loaded. It may not exist or you may not have permission to view it."
        />
      </div>
    );
  }

  const reportData = apiResponse.data;
  const historyRowData = location.state?.historyRow;

  const resolveBotAvatar = (row) => {
    if (!row) return "/images/bots/avatar_7.svg";
    const avatar = row.bot_avatar || row.botAvatar || row.avatar;
    if (avatar) {
      return `/images/bots/${avatar}`;
    }
    return "/images/bots/avatar_7.svg";
  };

  const botName =
    historyRowData?.bot_name ||
    historyRowData?.botName ||
    `${reportData?.bot_first_name || ""} ${reportData?.bot_last_name || ""}`.trim() ||
    "Coach AI Bot";
  const botRole = historyRowData?.bot_title || reportData?.bot_role || "AI Partner";
  const botCompany = historyRowData?.bot_company || reportData?.bot_company;
  const callType = historyRowData?.call_type || reportData?.call_type;
  const botAvatarSrc = resolveBotAvatar(historyRowData);

  // Transform data for components
  const transcriptMessages = reportData.transcript?.messages || [];
  const summaryData = reportData.summary || {};
  const scorecardsData = reportData.scorecards || {};
  const evaluationData = reportData.evaluation_criteria || [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white dark:bg-[#161B26] border-b border-gray-200 dark:border-[#22262F] -mx-8 -mt-8 px-6 py-4">
        <div className="mb-4">
          <button onClick={handleGoBack} className="flex items-center text-gray-600">
            <ArrowLeft className="w-4 h-4 mr-2" />
            <span className="text-sm font-semibold text-gray-600">Close</span>
          </button>
        </div>

        <div className="flex items-center space-x-3">
          <div className="flex items-center space-x-2">
            <Image src="/images/icons/untitledui/ai.svg" className="w-14 h-7 no-invert" alt="AI" />
            <h1 className="text-base font-semibold text-gray-900">Call with {botName}</h1>
          </div>
          <div className="h-6 w-px bg-gray-200" />
          <Badge variant="outline" className="bg-gray-50 text-gray-700 border-gray-200 font-normal">
            {botRole}
          </Badge>
          <div className="h-6 w-px bg-gray-200" />
          {callType && getCallTypeBadge(callType)}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:items-start">
        <TranscriptPanel
          messages={transcriptMessages}
          botName={botName}
          botRole={botRole}
          botAvatar={botAvatarSrc}
        />

        <SessionRightPanel
          summary={summaryData}
          scorecards={scorecardsData}
          evaluation={evaluationData}
          transcript={transcriptMessages}
        />
      </div>
    </div>
  );
}
